import type { PostManagerResponseDTO } from '@/api/generated/model';

// Row model — port of AdminPostDto.toRow (doc 08 §4.4). The DataGrid rows stay raw
// PostManagerResponseDTO; each cell derives its display value via toPostRow(row).
export interface PostRow {
  id: number;
  title: string; // content if non-blank, else displayName (CMP fallback)
  handle: string; // "@uname"
  thumbnail: string | null;
  fileType: string | null;
  viewCount: number; // per-type: P/N/E/I -> post, S -> story, F -> flick
  likeCount: number;
  type: string; // postType code
  postStatus: string | null;
  /** TipMi ledger state (PENDING/APPROVED/…); null when no post_moderation_publication row. */
  moderationStatus: string | null;
  isAddCategory: string | null;
  postProcessingError: string | null;
  bedrockLastAttemptAt: string | null;
  moderationLastError: string | null;
  moderationStartedAt: string | null;
  moderationDecidedAt: string | null;
  /** Coop combined risk 0–1; null when no ledger score. */
  combinedRisk: number | null;
  riskTier: string | null;
  releaseTime: string;
  earnings: number; // earnedAmount
}

// Post-family type codes that read the "post" view/like counters (doc 08 §4.4).
const postFamilies = new Set(['P', 'N', 'E', 'I']);

export function toPostRow(d: PostManagerResponseDTO): PostRow {
  const t = (d.postType ?? '').toUpperCase();
  const view = postFamilies.has(t)
    ? d.postViewCount
    : t === 'S'
      ? d.storyViewCount
      : t === 'F'
        ? d.flickViewCount
        : d.postViewCount;
  const like = postFamilies.has(t)
    ? d.postLikeCount
    : t === 'S'
      ? d.storyLikeCount
      : t === 'F'
        ? d.flickLikeCount
        : d.postLikeCount;
  const content = d.content?.trim();
  return {
    id: d.id ?? 0, // id is optional on the real DTO; default to keep PostRow.id a number
    title: content ? content : (d.displayName ?? ''),
    handle: d.uname ? `@${d.uname}` : '', // empty (not "@unknown") when the DTO has no handle

    thumbnail: d.thumbnail ?? null,
    fileType: d.fileType ?? null,
    viewCount: view ?? 0,
    likeCount: like ?? 0,
    type: d.postType ?? '',
    postStatus: d.postStatus ?? null,
    moderationStatus: d.moderationStatus ?? null,
    isAddCategory: d.isAddCategory ?? null,
    postProcessingError: d.postProcessingError ?? null,
    bedrockLastAttemptAt: d.bedrockLastAttemptAt ?? null,
    moderationLastError: d.moderationLastError ?? null,
    moderationStartedAt: d.moderationStartedAt ?? null,
    moderationDecidedAt: d.moderationDecidedAt ?? null,
    combinedRisk: d.combinedRisk ?? null,
    riskTier: d.riskTier ?? null,
    releaseTime: d.releaseTime ?? '',
    earnings: d.earnedAmount ?? 0,
  };
}
